import React from "react";
import Input from "get-life-storybook-ts/lib/atoms/Input/Input";
import SingleSelect from "get-life-storybook-ts/lib/components/Inputs/Input_Select/SingleSelect/SingleSelect";
import Typography from "get-life-storybook-ts/lib/atoms/Typography/Typography";
import BirthDateInput from "get-life-storybook-ts/lib/organisms/BirthDateInput/BirthDateInput";
import ButtonDS from "get-life-storybook-ts/lib/atoms/ButtonDS/ButtonDS";

import IndependentCalculatorController, {
  IndependentCalculatorPropsI,
} from "./IndependentCalculator.controller";

const IndependentCalculator: React.FC<IndependentCalculatorPropsI> = ({
  backgroundColor = "#F5F7FA",
}) => {
  const {
    actualStep,
    formData,
    formError,
    handleBlur,
    handleButton,
    handleChange,
    handleFocus,
    loading,
    optionsDailyCompensation,
    optionsYesNo,
    setActualStep,
    submitted,
    trackStepBack,
  } = IndependentCalculatorController();

  if (submitted) {
    return (
      <div
        className="w-full md:max-w-[450px] flex flex-col items-center justify-center pt-6 px-4 pb-8 gap-6 rounded-lg shadow-lg min-h-[300px]"
        style={{ backgroundColor }}
      >
        <Typography
          variant="Body"
          text="¡Gracias por tu solicitud!"
          color="purple"
          weight="700"
          className="text-[24px] leading-[32px] text-center"
        />
        <Typography
          variant="Body"
          text="Hemos recibido tus datos correctamente. Nos pondremos en contacto contigo lo antes posible."
          color="gray-text"
          className="text-center"
        />
      </div>
    );
  }

  const buttonLabel = actualStep < 3 ? "Continuar" : "Recibir presupuesto";

  return (
    <div
      className="w-full md:max-w-[450px] flex flex-col pt-6 px-4 pb-8 gap-8 rounded-lg shadow-lg"
      style={{ backgroundColor }}
    >
      <div className="flex flex-col justify-center items-center gap-6">
        {/* Step indicators */}
        <div className="flex flex-row gap-4 w-full">
          {[1, 2, 3].map((step, index) => (
            <React.Fragment key={step}>
              {index > 0 && (
                <Typography variant="BodySmall" text=">" color="ligth-gray" />
              )}
              <Typography
                variant="BodySmall"
                text={`Paso ${step}`}
                color={actualStep === step ? "purple" : "ligth-gray"}
                onClick={() => {
                  if (step < actualStep) {
                    trackStepBack(actualStep, step);
                    setActualStep(step);
                  }
                }}
                className={step < actualStep ? "cursor-pointer" : "cursor-default"}
              />
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Personal data */}
        {actualStep === 1 && (
          <>
            <BirthDateInput
              label="Fecha de Nacimiento"
              initialValue={formData.birthDate}
              handleFocus={() => handleFocus("birthDate")}
              setter={(birthDate: string) =>
                handleChange("birthDate", birthDate)
              }
              birthDateError={formError?.birthDate}
            />

            <div className="flex flex-col w-full">
              <Typography variant="Body" text="Email" color="gray-text" />
              <Input
                initialValue={formData.email}
                inputMode="email"
                autoComplete="email"
                onBlur={() => handleBlur("email")}
                onValueChange={(e: any) => handleChange("email", e)}
                placeholder="Escribe tu correo electrónico"
                initialError={formError?.email}
                id="independentEmailInput"
              />
            </div>

            <div className="flex flex-col w-full">
              <Typography variant="Body" text="Teléfono" color="gray-text" />
              <Input
                initialValue={formData.phone}
                inputMode="number"
                autoComplete="phone"
                pattern="\d*"
                onBlur={() => handleBlur("phone")}
                onValueChange={(e: any) => handleChange("phone", e)}
                placeholder="Escribe tu número de teléfono"
                initialError={formError?.phone}
                id="independentPhoneInput"
              />
            </div>

            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="Código Postal"
                color="gray-text"
              />
              <Input
                initialValue={formData.postalCode}
                onBlur={() => handleBlur("postalCode")}
                onValueChange={(e: any) => handleChange("postalCode", e)}
                placeholder="Escribe tu código postal"
                initialError={formError?.postalCode}
                id="independentPostalCodeInput"
              />
            </div>

            <div className="flex flex-col w-full">
              <Typography variant="Body" text="Localidad" color="gray-text" />
              <Input
                initialValue={formData.locality}
                onBlur={() => handleBlur("locality")}
                onValueChange={(e: any) => handleChange("locality", e)}
                placeholder="Escribe tu localidad"
                initialError={formError?.locality}
                id="independentLocalityInput"
              />
            </div>
          </>
        )}

        {/* Step 2: Professional data */}
        {actualStep === 2 && (
          <>
            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="¿Es empleado por cuenta propia?"
                color="gray-text"
              />
              <SingleSelect
                name="self_employed_select"
                initialValue={formData.isSelfEmployed}
                onValueChange={(e: any) =>
                  handleChange("isSelfEmployed", e)
                }
                options={optionsYesNo}
                defaultVariant="Standard"
                defaultSize="S"
              />
              {formError?.isSelfEmployed?.message && (
                <Typography
                  text={formError.isSelfEmployed.message}
                  variant="BodySmall"
                  weight="400"
                  className="text-red"
                />
              )}
            </div>

            <div className="flex flex-col w-full">
              <Typography variant="Body" text="Profesión" color="gray-text" />
              <Input
                initialValue={formData.profession}
                onBlur={() => handleBlur("profession")}
                onValueChange={(e: any) => handleChange("profession", e)}
                placeholder="Escribe tu profesión"
                initialError={formError?.profession}
                id="independentProfessionInput"
              />
            </div>

            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="Indemnización diaria deseada"
                color="gray-text"
              />
              <SingleSelect
                name="daily_compensation_select"
                initialValue={formData.dailyCompensation}
                onValueChange={(e: any) =>
                  handleChange("dailyCompensation", e)
                }
                options={optionsDailyCompensation}
                defaultVariant="Standard"
                defaultSize="S"
              />
              {formError?.dailyCompensation?.message && (
                <Typography
                  text={formError.dailyCompensation.message}
                  variant="BodySmall"
                  weight="400"
                  className="text-red"
                />
              )}
            </div>
          </>
        )}

        {/* Step 3: Health questions */}
        {actualStep === 3 && (
          <>
            <div className="flex flex-col w-full gap-2">
              <Typography
                variant="Body"
                text="Preguntas de salud"
                color="purple"
                weight="700"
                className="text-[20px] leading-[26px]"
              />
              <Typography
                variant="BodySmall"
                text="Por favor, contesta las siguientes preguntas con sinceridad."
                color="ligth-gray"
              />
            </div>

            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="¿Tiene previsto someterse a alguna intervención quirúrgica u hospitalización?"
                color="gray-text"
              />
              <SingleSelect
                name="surgery_select"
                initialValue={formData.hasSurgeryPlanned}
                onValueChange={(e: any) =>
                  handleChange("hasSurgeryPlanned", e)
                }
                options={optionsYesNo}
                defaultVariant="Standard"
                defaultSize="S"
              />
              {formError?.hasSurgeryPlanned?.message && (
                <Typography
                  text={formError.hasSurgeryPlanned.message}
                  variant="BodySmall"
                  weight="400"
                  className="text-red"
                />
              )}
            </div>

            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="¿Se encuentra actualmente en tratamiento por algún tipo de enfermedad?"
                color="gray-text"
              />
              <SingleSelect
                name="treatment_select"
                initialValue={formData.hasCurrentTreatment}
                onValueChange={(e: any) =>
                  handleChange("hasCurrentTreatment", e)
                }
                options={optionsYesNo}
                defaultVariant="Standard"
                defaultSize="S"
              />
              {formError?.hasCurrentTreatment?.message && (
                <Typography
                  text={formError.hasCurrentTreatment.message}
                  variant="BodySmall"
                  weight="400"
                  className="text-red"
                />
              )}
            </div>

            <div className="flex flex-col w-full">
              <Typography
                variant="Body"
                text="¿Padece alguna enfermedad de tipo crónico?"
                color="gray-text"
              />
              <SingleSelect
                name="chronic_select"
                initialValue={formData.hasChronicDisease}
                onValueChange={(e: any) =>
                  handleChange("hasChronicDisease", e)
                }
                options={optionsYesNo}
                defaultVariant="Standard"
                defaultSize="S"
              />
              {formError?.hasChronicDisease?.message && (
                <Typography
                  text={formError.hasChronicDisease.message}
                  variant="BodySmall"
                  weight="400"
                  className="text-red"
                />
              )}
            </div>
          </>
        )}

        <div className="flex flex-col w-full">
          <ButtonDS
            label={buttonLabel}
            onClick={() => handleButton()}
            disabled={loading}
            rightIcon="ArrowRightIcon"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default IndependentCalculator;
